import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, orderBy, query } from "firebase/firestore";

import DashboardLayout from "../../../components/layout/DashboardLayout";
import AceAvatar from "../../../components/shared/AceAvatar";
import { db } from "../../../firebase/firebase";

function normalizeStatus(status) {
  const value = String(status || "").toLowerCase();

  if (value === "neu" || value === "open" || value === "offen") return "open";
  return "done";
}

function getStatusLabel(status) {
  return normalizeStatus(status) === "open" ? "Offen" : "Erledigt";
}

function StatusPill({ status }) {
  const normalized = normalizeStatus(status);
  const map = {
    open: "is-open",
    done: "is-done",
  };

  return (
    <span className={`ac-feedback-statuspill ${map[normalized] || ""}`}>
      {getStatusLabel(status)}
    </span>
  );
}

function RatingPill({ value }) {
  const rating = Number(value || 0);
  let tone = "is-green";
  let label = `${rating}/5`;

  if (rating <= 2) {
    tone = "is-orange";
  } else if (rating === 3) {
    tone = "is-blue";
  }

  return <span className={`ac-feedback-tonepill ${tone}`}>{label}</span>;
}

function MiniKpi({ label, value, hint }) {
  return (
    <div className="ac-soft-panel ac-feedback-kpi">
      <div className="ac-feedback-kpi-label">{label}</div>
      <div className="ac-feedback-kpi-value">{value}</div>
      {hint ? <div className="ac-feedback-kpi-hint">{hint}</div> : null}
    </div>
  );
}

function parseCreatedAt(value) {
  if (!value) return null;
  if (value?.toDate && typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDate(value) {
  const parsed = parseCreatedAt(value);
  if (!parsed) return "—";
  return parsed.toLocaleDateString("de-AT");
}

export default function FeedbackPage() {
  const navigate = useNavigate();

  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [queryText, setQueryText] = useState("");
  const [selectedRole, setSelectedRole] = useState("Alle Rollen");
  const [selectedStatus, setSelectedStatus] = useState("Alle Status");
  const [selectedRating, setSelectedRating] = useState("Alle Bewertungen");

  useEffect(() => {
    let mounted = true;

    async function loadFeedback() {
      try {
        const snap = await getDocs(
          query(collection(db, "websiteFeedback"), orderBy("createdAt", "desc"))
        );

        const data = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        if (!mounted) return;
        setFeedbackList(data);
      } catch (error) {
        console.error("Feedback konnte nicht geladen werden:", error);
        if (!mounted) return;
        setFeedbackList([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadFeedback();

    return () => {
      mounted = false;
    };
  }, []);

  const roleOptions = useMemo(
    () => [
      "Alle Rollen",
      ...Array.from(
        new Set(feedbackList.map((f) => f.role).filter(Boolean))
      ),
    ],
    [feedbackList]
  );

  const ratingOptions = ["Alle Bewertungen", "1", "2", "3", "4", "5"];
  const statusOptions = ["Alle Status", "Offen", "Erledigt"];

  const filtered = useMemo(() => {
    return feedbackList.filter((item) => {
      const searchable = [
        item.name,
        item.role,
        item.message,
        item.page,
        getStatusLabel(item.status),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesText = queryText.trim()
        ? searchable.includes(queryText.trim().toLowerCase())
        : true;

      const matchesRole =
        selectedRole === "Alle Rollen" ? true : item.role === selectedRole;

      const matchesStatus =
        selectedStatus === "Alle Status"
          ? true
          : getStatusLabel(item.status) === selectedStatus;

      const matchesRating =
        selectedRating === "Alle Bewertungen"
          ? true
          : String(item.rating || "") === selectedRating;

      return matchesText && matchesRole && matchesStatus && matchesRating;
    });
  }, [feedbackList, queryText, selectedRole, selectedStatus, selectedRating]);

  const stats = useMemo(() => {
    return {
      open: feedbackList.filter((f) => normalizeStatus(f.status) === "open").length,
      lowRated: feedbackList.filter((f) => Number(f.rating || 0) <= 2).length,
      avgRating:
        feedbackList.length > 0
          ? (
              feedbackList.reduce((sum, item) => sum + Number(item.rating || 0), 0) /
              feedbackList.length
            ).toFixed(1)
          : "0.0",
      done: feedbackList.filter((f) => normalizeStatus(f.status) === "done").length,
    };
  }, [feedbackList]);

  return (
    <DashboardLayout title="Feedback">
      <div className="space-y-5">
        <section className="ac-soft-panel ac-feedback-hero">
          <div className="ac-feedback-hero-grid">
            <div>
              <h2 className="ac-feedback-hero-title">Feedback</h2>
              <p className="ac-feedback-hero-text">
                Übersicht über Rückmeldungen der Nutzer:innen und schnelle
                Orientierung bei offenen oder auffälligen Meldungen.
              </p>
            </div>

            <div className="ac-feedback-hero-side">
              <div className="ac-feedback-acebox">
                <AceAvatar variant="ruhig2" size={56} />
                <div className="ac-feedback-acebubble">
                  Ace sagt: Feedback hilft uns, AceTime gezielt weiterzuentwickeln.
                </div>
              </div>
            </div>
          </div>

          <div className="ac-feedback-kpi-grid">
            <MiniKpi label="Offene Feedbacks" value={stats.open} />
            <MiniKpi label="Kritische Bewertungen" value={stats.lowRated} />
            <MiniKpi label="Ø Bewertung" value={String(stats.avgRating).replace(".", ",")} />
            <MiniKpi label="Erledigt" value={stats.done} />
          </div>
        </section>

        <section className="ac-soft-panel ac-feedback-block">
          <div className="ac-feedback-block-head">
            <div>
              <div className="ac-feedback-block-title">Filter</div>
              <div className="ac-feedback-block-subtitle">
                Suche nach Name, Rolle, Nachricht oder Seite
              </div>
            </div>
          </div>

          <div className="ac-feedback-filtergrid">
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="ac-select"
            >
              {roleOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <select
              value={selectedRating}
              onChange={(e) => setSelectedRating(e.target.value)}
              className="ac-select"
            >
              {ratingOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="ac-select"
            >
              {statusOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <input
              type="text"
              placeholder="Suche nach Name, Nachricht oder Seite..."
              value={queryText}
              onChange={(e) => setQueryText(e.target.value)}
              className="ac-feedback-input"
            />
          </div>
        </section>

        <section className="ac-soft-panel ac-feedback-block">
          <div className="ac-feedback-tablewrap">
            <table className="ac-feedback-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Name</th>
                  <th>Rolle</th>
                  <th>Seite</th>
                  <th>Bewertung</th>
                  <th>Nachricht</th>
                  <th>Status</th>
                  <th>Aktion</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8}>
                      <div className="ac-feedback-empty">Lade Feedback...</div>
                    </td>
                  </tr>
                ) : filtered.length > 0 ? (
                  filtered.map((item) => {
                    const isOpen = normalizeStatus(item.status) === "open";

                    return (
                      <tr key={item.id}>
                        <td>{formatDate(item.createdAt)}</td>
                        <td className="font-semibold text-ac-text whitespace-nowrap">
                          {item.name || "—"}
                        </td>
                        <td>{item.role || "—"}</td>
                        <td>{item.page || "—"}</td>
                        <td>
                          <RatingPill value={item.rating} />
                        </td>
                        <td className="max-w-[320px]">
                          <div className="truncate text-ac-text">{item.message}</div>
                        </td>
                        <td>
                          <StatusPill status={item.status} />
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() => navigate(`/feedback/${item.id}`)}
                            className="ac-ghostbtn ac-feedback-tablebtn"
                          >
                            {isOpen ? "Antworten" : "Ansehen"}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8}>
                      <div className="ac-feedback-empty">
                        Keine Rückmeldungen für diese Filter gefunden.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}